// src/features/automacaodeemails/pages/SettingsPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { api } from "../api";

const ICONS = {
  save: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17,21 17,13 7,13 7,21" />
      <polyline points="7,3 7,8 15,8" />
    </svg>
  ),
  send: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22,2 15,22 11,13 2,9 22,2" />
    </svg>
  ),
  loader: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="ae2-spin">
      <line x1="12" y1="2" x2="12" y2="6" />
      <line x1="12" y1="18" x2="12" y2="22" />
    </svg>
  ),
};

const SETTING_GROUPS = [
  {
    title: "Envio (Graph/SMTP)",
    keys: ["email_transport", "graph_from_email"],
  },
  {
    title: "Servidor SMTP",
    keys: ["smtp_host", "smtp_port", "smtp_secure", "smtp_user", "smtp_pass", "smtp_from", "smtp_from_name"],
  },
  {
    title: "Telas do sistema",
    keys: [
      "screens_base_url",
      "screens_auto_seed",
      "screens_auto_sync_enabled",
      "screens_auto_sync_interval_min",
      "screens_auto_prune",
      "screens_auto_force",
    ],
  },
  {
    title: "Limites e performance",
    keys: ["max_elements_per_email", "max_recipients_per_email", "email_retry_attempts", "scan_timeout_ms", "capture_quality"],
  },
  {
    title: "Opcoes padrao",
    keys: ["default_attach_pdf", "puppeteer_headless"],
  },
];

export default function SettingsPage() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [changes, setChanges] = useState({});

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.settings.list();
      setSettings(res?.data || []);
    } catch (err) {
      console.error("Erro ao carregar configurações:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  function handleChange(key, value) {
    setChanges((prev) => ({ ...prev, [key]: value }));
  }

  function getValue(key) {
    if (changes[key] !== undefined) return changes[key];
    const setting = settings.find((s) => s.setting_key === key);
    return setting?.setting_value || "";
  }

  async function handleSave() {
    if (Object.keys(changes).length === 0) return;
    setSaving(true);
    try {
      await api.settings.bulk(changes);
      setChanges({});
      loadSettings();
      alert("Configurações salvas com sucesso!");
    } catch (err) {
      alert(`Erro ao salvar: ${err?.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleTestSmtp() {
    const defaultEmail = getValue("graph_from_email") || getValue("smtp_user");
    const testEmail = prompt("Digite um e-mail para teste:", defaultEmail);
    if (!testEmail) return;

    setTesting(true);
    try {
      await api.settings.testSmtp(testEmail);
      alert("E-mail de teste enviado com sucesso! Verifique sua caixa de entrada.");
    } catch (err) {
      alert(`Erro no teste SMTP: ${err?.message}`);
    } finally {
      setTesting(false);
    }
  }

  function renderInput(setting) {
    const key = setting.setting_key;
    const value = getValue(key);
    const type = setting.setting_type;
    const isPassword = key.includes("pass") || key.includes("secret");

    if (key === "email_transport") {
      return (
        <select value={value} onChange={(e) => handleChange(key, e.target.value)}>
          <option value="auto">Auto</option>
          <option value="graph">Graph</option>
          <option value="smtp">SMTP</option>
        </select>
      );
    }

    if (type === "boolean") {
      return (
        <select value={value} onChange={(e) => handleChange(key, e.target.value)}>
          <option value="true">Sim</option>
          <option value="false">Nao</option>
        </select>
      );
    }

    if (type === "number") {
      return (
        <input
          type="number"
          value={value}
          onChange={(e) => handleChange(key, e.target.value)}
        />
      );
    }

    return (
      <input
        type={isPassword ? "password" : "text"}
        value={value}
        onChange={(e) => handleChange(key, e.target.value)}
        placeholder={isPassword ? "********" : ""}
      />
    );
  }

  if (loading) {
    return (
      <div className="ae2-page">
        <div className="ae2-loading">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="ae2-page">
      <div className="ae2-page-header">
        <div>
          <h2>Configurações</h2>
          <p>Para Outlook corporativo, use os dados fornecidos pelo time de TI.</p>
        </div>
        <div className="ae2-actions">
          <button className="ae2-btn ae2-btn-ghost" onClick={handleTestSmtp} disabled={testing}>
            {testing ? ICONS.loader : ICONS.send} Testar envio
          </button>
          <button className="ae2-btn ae2-btn-primary" onClick={handleSave} disabled={saving || Object.keys(changes).length === 0}>
            {saving ? ICONS.loader : ICONS.save} Salvar
          </button>
        </div>
      </div>

      {Object.keys(changes).length > 0 && (
        <div className="ae2-info warning">
          Voce tem {Object.keys(changes).length} alteracao(oes) nao salva(s).
        </div>
      )}

      <div className="ae2-settings">
        {SETTING_GROUPS.map((group) => {
          const groupSettings = settings.filter((s) => group.keys.includes(s.setting_key));
          if (groupSettings.length === 0) return null;

          return (
            <div key={group.title} className="ae2-card ae2-settings-group">
              <div className="ae2-card-header">
                <h3>{group.title}</h3>
              </div>
              <div className="ae2-settings-list">
                {groupSettings.map((setting) => (
                  <div key={setting.setting_key} className="ae2-setting-row">
                    <div className="ae2-setting-label">
                      <div className="ae2-setting-key">{setting.setting_key}</div>
                      {setting.descricao && <div className="ae2-setting-desc">{setting.descricao}</div>}
                    </div>
                    <div className="ae2-setting-input">{renderInput(setting)}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
